import { useSession } from "@core/hooks/useSession";
import useUser from "@core/hooks/useUser";
import { XIcon } from "@heroicons/react/solid";
import Image from "next/image";
import { useRouter } from "next/router";
import { FC, ReactNode, useMemo, useState } from "react";

import useReassuranceCheck from "../components/NewPost/hooks/useReassuranceCheck";

type ImageData = {
  aspectRatio: number;
  url: string;
  publicId: string;
  link: string;
};

export const CONQUEROR_POST_DATA: ImageData = {
  aspectRatio: 4.23943661972,
  publicId:
    "Community%20Post%20Assets/therapy_milestone_ocd_conqueror_post_banner_dz0obk",
  link: "https://res.cloudinary.com/nocdcloud/image/upload/v1682005159/Community%20Post%20Assets/therapy_milestone_ocd_conqueror_post_banner_dz0obk.png",
  url: "https://res.cloudinary.com/nocdcloud/image/upload/v1682005159/Community%20Post%20Assets/therapy_milestone_ocd_conqueror_post_banner_dz0obk.png",
};

export const SUSTAINED_CONQUEROR_POST_DATA: ImageData = {
  publicId: "",
  url: "https://assets.treatmyocd.com/images/banners/badge_sustained_conqueror.png",
  link: "https://assets.treatmyocd.com/images/banners/badge_sustained_conqueror.png",
  aspectRatio: 3.573,
};

const ConquerorHeading: FC<{
  variant: "conqueror" | "sustained_conqueror";
}> = ({ variant }) => {
  if (variant === "sustained_conqueror") {
    return (
      <div className="text-center mx-auto max-w-[27rem] mb-4 space-y-4">
        <p className="text-24px font-bold">
          6 months of strength, we&apos;re here to help you keep going
        </p>

        <p className="text-14px">
          Reflect on your journey and share this pivotal milestone with the NOCD
          Community.
        </p>
      </div>
    );
  }
  if (variant === "conqueror") {
    return (
      <div className="text-center mx-auto max-w-[27rem] mb-4 pt-4 space-y-4">
        <p className="text-24px font-bold">You&apos;ve come so far!</p>

        <p className="text-14px">
          Write a Community post to celebrate and reflect on this important
          milestone and to give others hope.
        </p>
      </div>
    );
  }
  return null;
};

const getPlaceHolderText = ({
  conquerorPostData,
}: {
  conquerorPostData: {
    isConquerorPost: boolean;
    variant: "conqueror" | "sustained_conqueror";
  };
}) => {
  if (conquerorPostData?.isConquerorPost) {
    if (conquerorPostData?.variant === "sustained_conqueror") {
      return "Since conquering OCD, what can you do now?";
    }
    return "How do you see your life as being different from when you first started Therapy?";
  }
  return "Share details so the community can respond";
};

function BadgeImage({
  conquerorPostData,
  url,
  aspectRatio,
  handleClick,
}: {
  conquerorPostData: {
    isConquerorPost: boolean;
    variant: "conqueror" | "sustained_conqueror";
  };
  url: string;
  aspectRatio?: string;
  handleClick: () => void;
}): JSX.Element {
  if (conquerorPostData?.isConquerorPost) {
    if (conquerorPostData?.variant === "conqueror") {
      return (
        <div className="overflow-hidden pl-22 tablet:pl-28 tablet:py-4 tablet:pr-6 flex items-center justify-between rounded-lg py-1 pr-4 relative">
          <Image
            src="/images/community/conqueror-preview.svg"
            layout="fill"
            objectFit="cover"
            objectPosition="left center"
            alt=""
            className="z-0"
          />

          <p className="font-bold text-black relative z-1">
            I received my OCD Conqueror badge!
          </p>

          <XIcon
            onClick={handleClick}
            className="tablet:ml-12 ml-6 h-5 w-5 cursor-pointer relative z-1"
          />
        </div>
      );
    }
    return (
      <div className="overflow-hidden pl-22 tablet:pl-28 tablet:py-4 tablet:pr-6 flex items-center justify-between rounded-lg py-1 pr-4 relative">
        <Image
          src="/images/community/sustained-conqueror-preview.png"
          layout="fill"
          objectFit="cover"
          objectPosition="left center"
          alt=""
          className="z-0"
        />

        <p className="font-bold text-white z-1 relative">
          I&apos;ve been an OCD Conqueror for 6+ months!
        </p>

        <XIcon
          onClick={handleClick}
          className="tablet:ml-12 ml-6 h-5 w-5 cursor-pointer relative z-1 text-white"
        />
      </div>
    );
  }
  if (url) {
    return (
      <div
        style={{
          aspectRatio: aspectRatio ? `${aspectRatio}/1` : "1.91/1",
          position: "relative",
          width: "100%",
        }}
      >
        <Image
          src={url}
          layout="fill"
          objectFit="cover"
          objectPosition="left center"
          alt=""
          className="z-0"
        />

        <XIcon
          onClick={handleClick}
          className="absolute top-4 right-4 h-5 w-5 cursor-pointer bg-black rounded-full z-1 text-white"
        />
      </div>
    );
  }
}

interface BadgePostStore {
  isBadgePost: boolean;
  setIsBadgePost: (isConquerorPost: boolean) => void;
  isBadgePostEnabled: boolean;
  variant: "conqueror" | "sustained_conqueror";
  headingCopy?: ReactNode | null;
  bodyPlaceholder?: string | null;
  prefilledTitle?: string | null;
  badgeImage: JSX.Element;
  imageData: ImageData | null;
}

const useIsBadgePost = (): BadgePostStore => {
  const router = useRouter();
  const { data: session } = useSession();
  const { data: user } = useUser();
  const { data: reassuranceCheckData } = useReassuranceCheck(
    session?.accessToken ?? "",
    router?.query?.new_post_type
      ? String(router?.query?.new_post_type)
      : undefined
  );

  const isEnabled = useMemo(
    () =>
      ((router?.query?.conqueror_post === "1" ||
        router?.query?.sustained_conqueror_post === "1") &&
        user?.userBadges?.includes("OCD_CONQUEROR")) ||
      !!router?.query?.new_post_type,
    [
      router?.query?.conqueror_post,
      router?.query?.sustained_conqueror_post,
      router?.query?.new_post_type,
      user?.userBadges,
    ]
  );

  const isSustained = useMemo(
    () => router?.query?.sustained_conqueror_post === "1",
    [router?.query?.sustained_conqueror_post]
  );

  const headingCopy = useMemo(() => {
    if (router?.query?.conqueror_post || isSustained) {
      return (
        <ConquerorHeading
          variant={isSustained ? "sustained_conqueror" : "conqueror"}
        />
      );
    }

    return null;
  }, [isSustained, router?.query?.conqueror_post]);

  const bodyPlaceholder = useMemo(() => {
    if (router?.query?.conqueror_post || isSustained) {
      return getPlaceHolderText({
        conquerorPostData: {
          isConquerorPost: !!router?.query?.conqueror_post,
          variant: isSustained ? "sustained_conqueror" : "conqueror",
        },
      });
    }

    if (reassuranceCheckData?.type_data?.body_placeholder) {
      return reassuranceCheckData?.type_data?.body_placeholder;
    }
    return null;
  }, [
    isSustained,
    router?.query?.conqueror_post,
    reassuranceCheckData?.type_data?.body_placeholder,
  ]);

  const prefilledTitle = useMemo(() => {
    if (reassuranceCheckData?.type_data?.title_prefilled_text) {
      return reassuranceCheckData?.type_data?.title_prefilled_text;
    }
    return null;
  }, [reassuranceCheckData?.type_data?.title_prefilled_text]);

  const [isBadgePost, setIsBadgePost] = useState(isEnabled || false);

  const imageData: ImageData = useMemo(() => {
    if (isSustained) {
      return SUSTAINED_CONQUEROR_POST_DATA;
    }
    if (router?.query?.conqueror_post) {
      return CONQUEROR_POST_DATA;
    }
    return {
      publicId: "",
      url: reassuranceCheckData?.type_data?.image_data?.url,
      link: reassuranceCheckData?.type_data?.image_data?.url,
      aspectRatio: Number(
        reassuranceCheckData?.type_data?.image_data?.aspect_ratio
      ),
    };
  }, [
    isSustained,
    router?.query?.conqueror_post,
    reassuranceCheckData?.type_data?.image_data?.url,
    reassuranceCheckData?.type_data?.image_data?.aspect_ratio,
  ]);

  const variant = useMemo(() => {
    if (isSustained) {
      return "sustained_conqueror";
    }
    if (router?.query?.conqueror_post) {
      return "conqueror";
    }
    return null;
  }, [isSustained, router?.query?.conqueror_post]);

  return {
    isBadgePost,
    setIsBadgePost,
    isBadgePostEnabled: isEnabled || false,
    variant,
    headingCopy,
    bodyPlaceholder,
    prefilledTitle,
    badgeImage: (
      <BadgeImage
        url={reassuranceCheckData?.type_data?.image_data?.url}
        aspectRatio={reassuranceCheckData?.type_data?.image_data?.aspect_ratio}
        handleClick={() => setIsBadgePost(false)}
        conquerorPostData={{
          isConquerorPost: !!router?.query?.conqueror_post,
          variant: isSustained ? "sustained_conqueror" : "conqueror",
        }}
      />
    ),
    imageData,
  };
};

export default useIsBadgePost;
